import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { RuleGroup } from '@crm/shared';

export interface ListParams {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  q?: string;
  /** JSON-encoded RuleGroup, as sent to the API */
  filter?: string;
}

/** Table state (paging, sort, search, filter) kept in the URL so views are shareable and survive reloads. */
export function useListParams(defaults: Partial<ListParams> = {}) {
  const [sp, setSp] = useSearchParams();
  const params = useMemo<ListParams>(
    () => ({
      page: Number(sp.get('page') ?? 1) || 1,
      pageSize: Number(sp.get('pageSize') ?? defaults.pageSize ?? 25) || 25,
      sortBy: sp.get('sortBy') ?? defaults.sortBy,
      sortDir: (sp.get('sortDir') as 'asc' | 'desc' | null) ?? defaults.sortDir,
      q: sp.get('q') ?? undefined,
      filter: sp.get('filter') ?? undefined,
    }),
    [sp, defaults.pageSize, defaults.sortBy, defaults.sortDir],
  );

  const setParams = useCallback(
    (patch: Partial<ListParams>) => {
      const next = new URLSearchParams(sp);
      const merged = { ...params, ...patch };
      if (patch.q !== undefined || patch.filter !== undefined || patch.pageSize !== undefined) merged.page = patch.page ?? 1;
      for (const [k, v] of Object.entries(merged)) {
        if (v === undefined || v === '' || v === null) next.delete(k);
        else next.set(k, String(v));
      }
      setSp(next, { replace: true });
    },
    [sp, params, setSp],
  );

  const filterGroup = useMemo<RuleGroup | undefined>(() => {
    if (!params.filter) return undefined;
    try {
      return JSON.parse(params.filter) as RuleGroup;
    } catch {
      return undefined;
    }
  }, [params.filter]);

  const setFilterGroup = useCallback(
    (g: RuleGroup | undefined) => setParams({ filter: g && g.conditions.length ? JSON.stringify(g) : undefined }),
    [setParams],
  );

  return { params, setParams, filterGroup, setFilterGroup };
}
